// An attack against a target (issue 1124; docs/VTT/design/29-the-attack-chain.md §2): the attack as asked for, the
// facts the server stamps on the d20 it throws, the outcome worked out from them, and the version of it each person
// may see (§6). The attack IS a roll in the log — the dice bag's record with an `attack` beside it — so privacy, the
// DM's fudge and band, the cleared view and the saved file are the roll's own. Nothing here throws a die: the server
// hands the faces in. The browser works an outcome out only from a record it was sent, never one it made up.
//
// The rules are the table's system's (issue 1303; `rules/system.ts`): the server reads them as it commits the attack and
// stamps the part the record needs (`AttackRuling`) into it, so the outcome, the lines and every replay are worked out
// from the record alone, never from rules read later. A record from before the stamp is read by
// `legacy/records.ts` as the rules it was made with.
import { applyStops, parseDamageStops, stopLineText } from './damageStops.js';
import { parseBand } from '../dice/dice.js';
import { legacyAttackRuling } from '../legacy/records.js';
import { diceRange, diceText, mainDefence, NUMBER_LABEL, parseTableDice, parseTiers, sourceOf } from './system.js';
import { addedModifier, poolResult, poolText } from '../dice/pool.js';
import { advantageText, isAdvantageAsk } from './advantage.js';
import { parseStagedFacts, playerStaged, stagedDamage, stagedLines, stagedOutcome } from './staged.js';
import { castDamage, castLines, castOutcome, parseCastFacts, playerCast } from './cast.js';
/** A weapon's special abilities on the wire (qa-runs#1159): at most this many, each a slug. */
export const ABILITY_LIMITS = { count: 3, slug: 128 };
const ABILITY_RE = /^[a-z0-9][a-z0-9-]*$/;
export const FULL_SWINGS_MAX = 16;
const RULING_PARTS = ['dice', 'natural', 'critical'];
export const CHANGED_RESULTS = ['hit', 'miss', 'critical'];
/** The kind of damage every hit deals unless the rules say otherwise (docs/MRCS/combat-rules.md `damage.minimum.kind`). */
export const PLAIN_DAMAGE = 'lethal';
export const ATTACK_LIMITS = { name: 80, terms: 12, termLabel: 40, termValue: 1000, parts: 6, count: 20, sides: 100, partLabel: 24, natural: 20_000, multiplier: 10, ruleWord: 60 };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isText = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max;
const isDefence = (v) => typeof v === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(v);
/** The defence the attack is compared with (issue 1126): the stamped one changed by the target's conditions and effects. */
export const effectiveAc = (f) => (f.ac === null ? null : f.ac + (f.acTerms ?? []).reduce((s, t) => s + t.value, 0));
/** The sum of an attack's terms: the d20's modifier. */
export const bonusOf = (terms) => terms.reduce((s, t) => s + t.value, 0);
/** A damage spec from anywhere: exact shape or null. Dice from d2 to d100, up to twenty of a kind (issue 1132). */
export function parseDamageSpec(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const d = v;
    if (!Array.isArray(d.parts) || d.parts.length < 1 || d.parts.length > ATTACK_LIMITS.parts || !isInt(d.bonus, -ATTACK_LIMITS.termValue, ATTACK_LIMITS.termValue))
        return null;
    const parts = [];
    for (const p of d.parts) {
        if (typeof p !== 'object' || p === null)
            return null;
        const { count, sides, label, extra, types } = p;
        if (!isInt(count, 1, ATTACK_LIMITS.count) || !isInt(sides, 2, ATTACK_LIMITS.sides))
            return null;
        if (label !== undefined && !isText(label, ATTACK_LIMITS.partLabel))
            return null;
        if (extra !== undefined && typeof extra !== 'boolean')
            return null;
        if (types !== undefined && !(Array.isArray(types) && types.length <= 4 && types.every((t) => isText(t, ATTACK_LIMITS.partLabel))))
            return null;
        parts.push({ count, sides, ...(label !== undefined ? { label: label.trim() } : {}), ...(extra ? { extra: true } : {}), ...(types?.length ? { types: [...types] } : {}) });
    }
    let once;
    if (d.once !== undefined) {
        if (!Array.isArray(d.once) || d.once.length < 1 || d.once.length > ATTACK_LIMITS.parts)
            return null;
        once = [];
        for (const t of d.once) {
            const { label, value } = (t ?? {});
            if (!isText(label, ATTACK_LIMITS.termLabel) || !isInt(value, 1, ATTACK_LIMITS.termValue))
                return null;
            once.push({ label: label.trim(), value: value });
        }
    }
    const more = once ? { once } : {};
    if (d.terms === undefined)
        return { parts, bonus: d.bonus, ...more };
    const terms = damageTermsOf(d.terms, d.bonus);
    return terms ? { parts, bonus: d.bonus, terms, ...more } : null;
}
/**
 * A damage bonus's lines (issue 1506), exactly: 1 to `ATTACK_LIMITS.terms - 3` (a sheet's base and its modifiers), each
 * a label of plain text and a whole number in bounds, adding up to `bonus`; else null.
 */
export function damageTermsOf(v, bonus) {
    // eslint-disable-next-line no-control-regex
    const plain = (t) => isText(t, ATTACK_LIMITS.termLabel) && !/[\u0000-\u001f\u007f]/.test(t);
    if (!Array.isArray(v) || v.length < 1 || v.length > ATTACK_LIMITS.terms - 3)
        return null;
    const out = [];
    for (const t of v) {
        if (!isObjRec(t) || !plain(t.label) || !isInt(t.value, -ATTACK_LIMITS.termValue, ATTACK_LIMITS.termValue))
            return null;
        out.push({ label: t.label.trim(), value: t.value });
    }
    return out.reduce((s, t) => s + t.value, 0) === bonus ? out : null;
}
/**
 * Damage as printed or typed (§2.7, §8.1): "2d6 + 3", "1d8+1", "2d6 + 2 plus 2d6 fire". The first dice are the weapon's
 * (multiplied on a critical); each "plus NdS word" adds extra dice, never multiplied. Words that are not dice ("plus
 * poison") are left to the DM. Null when it does not start with dice.
 */
export function parseDamageText(text) {
    const m = /^\s*(\d+)\s*d\s*(\d+)(?:\s*([+-])\s*(\d+))?/i.exec(text);
    if (!m)
        return null;
    // The dice's type where the text says one: "1d8+3 slashing", "6d6 fire", "1d6 piercing/slashing".
    const said = /^\s*((?!plus\b)[a-z]+(?:[ /](?!plus\b)[a-z]+)*)/i.exec(text.slice(m[0].length));
    const word = said ? said[1].toLowerCase().slice(0, ATTACK_LIMITS.partLabel) : null;
    const parts = [{ count: Number(m[1]), sides: Number(m[2]), ...(word ? { label: word } : {}) }];
    const bonus = m[3] ? (m[3] === '-' ? -1 : 1) * Number(m[4]) : 0;
    for (const x of text.slice(m[0].length).matchAll(/plus\s+(\d+)\s*d\s*(\d+)\s*([a-z]+(?: (?!plus\b)[a-z]+)*)?/gi)) {
        parts.push({ count: Number(x[1]), sides: Number(x[2]), ...(x[3] ? { label: x[3].toLowerCase().slice(0, ATTACK_LIMITS.partLabel) } : {}), extra: true });
    }
    return parseDamageSpec({ parts, bonus });
}
/** "2d6 + 3", "2d6 + 2 + 2d6 fire": a damage spec as words. */
export function damageText(d) {
    const [first, ...rest] = d.parts;
    const head = first ? `${first.count}d${first.sides}${first.label ? ` ${first.label}` : ''}` : '';
    return `${head}${d.bonus ? ` ${signed(d.bonus)}` : ''}${rest.map((p) => ` + ${p.count}d${p.sides}${p.label ? ` ${p.label}` : ''}`).join('')}`;
}
/** An attack spec from anywhere (the wire, a stored record): exact shape or null. */
export function parseAttackSpec(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const a = v;
    if (!isText(a.name, ATTACK_LIMITS.name))
        return null;
    if (!Array.isArray(a.terms) || a.terms.length > ATTACK_LIMITS.terms)
        return null;
    const terms = [];
    for (const t of a.terms) {
        if (typeof t !== 'object' || t === null)
            return null;
        const { label, value, aboutTarget, secret, shown } = t;
        if (!isText(label, ATTACK_LIMITS.termLabel) || !isInt(value, -ATTACK_LIMITS.termValue, ATTACK_LIMITS.termValue))
            return null;
        if (aboutTarget !== undefined && typeof aboutTarget !== 'boolean')
            return null;
        if (secret !== undefined && typeof secret !== 'boolean')
            return null;
        if (shown !== undefined && typeof shown !== 'boolean')
            return null;
        terms.push({ label: label.trim(), value, ...(aboutTarget ? { aboutTarget: true } : {}), ...(secret ? { secret: true } : {}), ...(shown ? { shown: true } : {}) });
    }
    if (a.threat !== undefined && !isInt(a.threat, 1, ATTACK_LIMITS.natural))
        return null;
    if (a.multiplier !== undefined && !isInt(a.multiplier, 2, ATTACK_LIMITS.multiplier))
        return null;
    if (!isDefence(a.defence))
        return null;
    if (a.concealment !== undefined && !isInt(a.concealment, 1, 100))
        return null;
    if (a.images !== undefined && !isInt(a.images, 1, 8))
        return null;
    if (a.ranged !== undefined && typeof a.ranged !== 'boolean')
        return null;
    if (a.twoHanded !== undefined && typeof a.twoHanded !== 'boolean')
        return null;
    if (a.offHand !== undefined && typeof a.offHand !== 'boolean')
        return null;
    const damage = a.damage === undefined ? null : parseDamageSpec(a.damage);
    if (a.damage !== undefined && !damage)
        return null;
    if (a.abilities !== undefined && !(Array.isArray(a.abilities) && a.abilities.length <= ABILITY_LIMITS.count && a.abilities.every((x) => typeof x === 'string' && x.length <= ABILITY_LIMITS.slug && ABILITY_RE.test(x))))
        return null;
    const abilities = Array.isArray(a.abilities) && a.abilities.length ? [...a.abilities] : null;
    return {
        name: a.name.trim(), terms, ...(a.threat !== undefined ? { threat: a.threat } : {}), ...(a.multiplier !== undefined ? { multiplier: a.multiplier } : {}), defence: a.defence,
        ...(a.concealment !== undefined ? { concealment: a.concealment } : {}), ...(a.images !== undefined ? { images: a.images } : {}), ...(a.improvedCover === true ? { improvedCover: true } : {}), ...(damage ? { damage } : {}),
        ...(a.ranged ? { ranged: true } : {}), ...(a.twoHanded ? { twoHanded: true } : {}), ...(a.offHand ? { offHand: true } : {}),
        ...(abilities ? { abilities } : {}),
    };
}
/**
 * A damage roll with more lines added to its bonus (issue 1508: Power Attack's +3, Point Blank Shot's +1), each its own
 * line after the roll's own (its sheet lines, or its bonus as `base`); past what a roll holds, the last lines are one.
 */
export function withDamageTerms(d, more) {
    const add = more.filter((t) => t.value !== 0);
    if (!add.length)
        return d;
    const max = ATTACK_LIMITS.terms - 3;
    const lines = [...(d.terms ?? (d.bonus ? [{ label: 'base', value: d.bonus }] : [])), ...add.map((t) => ({ label: t.label.slice(0, ATTACK_LIMITS.termLabel), value: t.value }))];
    const kept = lines.length <= max ? lines : [...lines.slice(0, max - 1), { label: 'other', value: lines.slice(max - 1).reduce((n, t) => n + t.value, 0) }];
    const bonus = Math.max(-ATTACK_LIMITS.termValue, Math.min(ATTACK_LIMITS.termValue, d.bonus + add.reduce((n, t) => n + t.value, 0)));
    return { ...d, bonus, ...(kept.reduce((n, t) => n + t.value, 0) === bonus ? { terms: kept } : {}) };
}
/**
 * A damage roll with the weapon's damage again (an effect's `weapon_dice`, `weapon_damage`): the weapon's own dice
 * `value` more times, as extra dice a critical does not multiply, named by the effect; `whole`, its bonus as many times
 * too, as a line added once. Dice that are not the weapon's (fire, sneak attack) are not rolled again.
 */
export function withWeaponAgain(d, again) {
    const parts = [...d.parts];
    const once = [...(d.once ?? [])];
    for (const a of again) {
        if (a.value <= 0)
            continue;
        const label = a.label.slice(0, ATTACK_LIMITS.partLabel);
        for (const p of d.parts)
            if (!p.extra && parts.length < ATTACK_LIMITS.parts)
                parts.push({ count: Math.min(ATTACK_LIMITS.count, p.count * a.value), sides: p.sides, label, extra: true });
        if (a.whole && d.bonus > 0 && once.length < ATTACK_LIMITS.parts)
            once.push({ label: a.label.slice(0, ATTACK_LIMITS.termLabel), value: Math.min(ATTACK_LIMITS.termValue, d.bonus * a.value) });
    }
    return { ...d, parts, ...(once.length ? { once } : {}) };
}
/** A damage with extra dice added, as a part a critical does not multiply (sneak attack); past what a roll holds, none. */
export function withExtraDice(d, count, sides, label) {
    if (d.parts.length >= ATTACK_LIMITS.parts)
        return d;
    return { ...d, parts: [...d.parts, { count: Math.min(ATTACK_LIMITS.count, count), sides, label: label.toLowerCase(), extra: true }] };
}
/** A damage dealt so many times over (a charge's feat, qa-runs#366): its dice and bonus; extra dice count once, as on a critical. */
export function timesDamage(d, n) {
    return {
        ...d, parts: d.parts.map((p) => (p.extra ? p : { ...p, count: Math.min(p.count * n, ATTACK_LIMITS.count) })), bonus: Math.max(-ATTACK_LIMITS.termValue, Math.min(ATTACK_LIMITS.termValue, d.bonus * n)),
        ...(d.terms ? { terms: d.terms.map((t) => ({ ...t, value: t.value * n })) } : {}),
    };
}
/** An attack request from the wire: exact shape or null. Untrusted input, always. */
export function parseAttackRequest(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const b = v;
    if (b.kind !== 'attack.request' || !UUID_RE.test(String(b.attacker)) || !UUID_RE.test(String(b.target)) || b.attacker === b.target)
        return null;
    const attack = parseAttackSpec(b.attack);
    const priv = b.private === undefined ? false : b.private;
    if (!attack || typeof priv !== 'boolean')
        return null;
    const band = b.band === undefined || b.band === null ? null : parseBand(b.band);
    if (b.band !== undefined && b.band !== null && !band)
        return null;
    const rulings = b.rulings;
    if (rulings !== undefined && (!Array.isArray(rulings) || rulings.length > 30 || !rulings.every((x) => typeof x === 'string' && UUID_RE.test(x)) || new Set(rulings).size !== rulings.length))
        return null;
    if (b.extra !== undefined && b.extra !== true)
        return null;
    if (b.anyway !== undefined && b.anyway !== true)
        return null;
    let full;
    if (b.full !== undefined) {
        if (!Array.isArray(b.full) || b.full.length < 1 || b.full.length > FULL_SWINGS_MAX || b.extra !== undefined || b.leftover !== undefined)
            return null;
        full = [];
        for (const x of b.full) {
            const sw = parseAttackSpec(x);
            if (!sw)
                return null;
            full.push(sw);
        }
    }
    if (b.extraAt !== undefined && (!full || !isInt(b.extraAt, 1, full.length - 1)))
        return null;
    if (b.leftover !== undefined && (!UUID_RE.test(String(b.leftover)) || b.extra !== undefined || band))
        return null;
    if (b.option !== undefined && (typeof b.option !== 'string' || !/^[a-z][a-z0-9_]{0,62}$/.test(b.option) || b.extra !== undefined || b.leftover !== undefined))
        return null;
    if (b.stance !== undefined && (typeof b.stance !== 'string' || !/^[a-z][a-z0-9_]{0,62}$/.test(b.stance)))
        return null;
    if (b.advantage !== undefined && !isAdvantageAsk(b.advantage))
        return null;
    const situations = b.situations;
    if (situations !== undefined && (!Array.isArray(situations) || situations.length > 24 || !situations.every((x) => typeof x === 'string' && /^[a-z][a-z0-9_]{0,62}$/.test(x)) || new Set(situations).size !== situations.length))
        return null;
    return {
        kind: 'attack.request', attacker: String(b.attacker), target: String(b.target), attack, private: priv, ...(b.stance !== undefined ? { stance: b.stance } : {}), ...(band ? { band } : {}), ...(rulings && rulings.length ? { rulings: rulings.map(String) } : {}), ...(b.extra ? { extra: true } : {}),
        ...(full ? { full } : {}), ...(b.extraAt !== undefined ? { extraAt: b.extraAt } : {}), ...(b.leftover !== undefined ? { leftover: String(b.leftover) } : {}),
        ...(typeof b.option === 'string' ? { option: b.option } : {}), ...(b.anyway ? { anyway: true } : {}),
        ...(b.advantage !== undefined ? { advantage: b.advantage } : {}), ...(situations && situations.length ? { situations: situations.map(String) } : {}),
    };
}
function parseSide(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const s = v;
    if (!UUID_RE.test(String(s.id)) || typeof s.name !== 'string' || s.name.length > 200 || typeof s.seen !== 'boolean' || typeof s.named !== 'boolean')
        return null;
    if (!Array.isArray(s.owners) || !s.owners.every((o) => UUID_RE.test(String(o))))
        return null;
    if (s.ownersSee !== undefined && typeof s.ownersSee !== 'boolean')
        return null;
    if (s.seenBy !== undefined && (!Array.isArray(s.seenBy) || s.seenBy.length > 64 || !s.seenBy.every((o) => UUID_RE.test(String(o)))))
        return null;
    return { id: String(s.id), name: s.name, seen: s.seen, named: s.named, owners: s.owners.map(String), ...(s.ownersSee === false ? { ownersSee: false } : {}), ...(Array.isArray(s.seenBy) && s.seenBy.length ? { seenBy: s.seenBy.map(String) } : {}) };
}
/** A stamped ruling: exact shape or null. */
function parseRuling(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const r = v;
    const dice = parseTableDice(r.dice);
    if (!dice)
        return null;
    const { min, max } = diceRange(dice);
    if (!Array.isArray(r.natural) || r.natural.length > 24)
        return null;
    const natural = [];
    for (const n of r.natural) {
        const x = n;
        if (!x || !isInt(x.face, min, max) || (x.result !== 'hit' && x.result !== 'miss'))
            return null;
        natural.push({ face: x.face, result: x.result });
    }
    const c = r.critical;
    if (c !== null && (typeof c !== 'object' || (c.confirm !== 'roll_again' && c.confirm !== 'none') || (c?.multiplies !== undefined && c.multiplies !== 'dice')))
        return null;
    // A success count has no natural face and no critical (issue 1312).
    if (dice.success && (natural.length || c))
        return null;
    const tiers = r.tiers === undefined ? null : parseTiers(r.tiers);
    if (r.tiers !== undefined && !tiers)
        return null;
    if (r.minimumDamage !== null && !isInt(r.minimumDamage, 0, 1000))
        return null;
    if (r.minimumKind !== undefined && !isDefence(r.minimumKind))
        return null;
    const w = r.words;
    const word = (k) => (w && isText(w[k], ATTACK_LIMITS.ruleWord) ? w[k] : null);
    const words = { hit: word('hit'), miss: word('miss'), critical: word('critical'), threat: word('threat') };
    if (!isText(r.defence, ATTACK_LIMITS.ruleWord) || Object.values(words).some((x) => x === null))
        return null;
    let from;
    if (r.from !== undefined) {
        const fr = r.from;
        if (typeof fr !== 'object' || fr === null || Array.isArray(fr))
            return null;
        from = {};
        for (const [k, x] of Object.entries(fr)) {
            const e = x;
            if (!RULING_PARTS.includes(k) || !e || !isText(e.name, 120) || typeof e.shown !== 'boolean')
                return null;
            from[k] = { name: e.name, shown: e.shown };
        }
    }
    if (r.borrowed !== undefined && !isText(r.borrowed, 120))
        return null;
    if ((r.against !== undefined && r.against !== 'number') || (r.marginToDamage !== undefined && r.marginToDamage !== true))
        return null;
    return { ...(r.against === 'number' ? { against: 'number' } : {}), ...(r.marginToDamage === true ? { marginToDamage: true } : {}), dice, natural, critical: c ? { confirm: c.confirm, ...(c.multiplies === 'dice' ? { multiplies: 'dice' } : {}) } : null, minimumDamage: r.minimumDamage, ...(typeof r.minimumKind === 'string' ? { minimumKind: r.minimumKind } : {}), defence: r.defence, words: words, ...(tiers ? { tiers } : {}), ...(from && Object.keys(from).length ? { from } : {}), ...(typeof r.borrowed === 'string' ? { borrowed: r.borrowed } : {}) };
}
/** Stored facts (every stored body is read through a parser on replay): exact shape or null. One from before issue 1303 gets the rules it was made with. */
export function parseAttackFacts(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const f = v;
    const spec = parseAttackSpec(f);
    const attacker = parseSide(f.attacker);
    const target = parseSide(f.target);
    if (!spec || !attacker || !target)
        return null;
    const rules = f.rules === undefined ? legacyAttackRuling(spec.defence) : parseRuling(f.rules);
    if (!rules)
        return null;
    const { min, max } = diceRange(rules.dice);
    // A system with criticals stamps the threat range and multiplier the attack was made with; one confirming by a second
    // roll, the confirmation's natural result too.
    if (rules.critical && (spec.threat === undefined || spec.multiplier === undefined))
        return null;
    if ((rules.critical?.confirm === 'roll_again') !== (f.confirm !== undefined) || (f.confirm !== undefined && !isInt(f.confirm, min, max)))
        return null;
    if (f.ac !== null && !isInt(f.ac, -100, 1000))
        return null;
    if (f.note !== undefined && (typeof f.note !== 'string' || f.note.length > 200))
        return null;
    // The miss chance's d100 rides with a concealment, and the damage dice with a damage spec: each the other's shape.
    if ((spec.concealment === undefined) !== (f.missRoll === undefined) || (f.missRoll !== undefined && !isInt(f.missRoll, 1, 100)))
        return null;
    if (f.missAgain !== undefined && (f.missRoll === undefined || !isInt(f.missAgain, 1, 100)))
        return null;
    if ((spec.images === undefined) !== (f.imageRoll === undefined) || (f.imageRoll !== undefined && !isInt(f.imageRoll, 1, 9)) || (f.imageNear !== undefined && !isInt(f.imageNear, 1, 100)))
        return null;
    let damageRoll;
    if (spec.damage || f.damageRoll !== undefined) {
        const r = f.damageRoll;
        const dice = spec.damage;
        const faceList = (v, n, sides) => Array.isArray(v) && v.length === n && v.every((x) => isInt(x, 1, sides));
        const faces = r?.faces;
        const crit = r?.crit;
        if (!dice || !Array.isArray(faces) || !Array.isArray(crit) || faces.length !== dice.parts.length || crit.length !== dice.parts.length)
            return null;
        const extraRolls = rules.critical ? (spec.multiplier ?? 1) - 1 : 0;
        const ok = dice.parts.every((p, i) => faceList(faces[i], p.count, p.sides) && faceList(crit[i], p.extra && !rules.critical?.multiplies ? 0 : p.count * extraRolls, p.sides));
        if (!ok)
            return null;
        damageRoll = { faces: faces.map((x) => [...x]), crit: crit.map((x) => [...x]) };
    }
    if (f.scene !== undefined && !UUID_RE.test(String(f.scene)))
        return null;
    const acTerms = f.acTerms === undefined ? null : parseAttackSpec({ name: 'x', terms: f.acTerms, defence: 'x' })?.terms ?? undefined;
    if (f.acTerms !== undefined && !acTerms)
        return null;
    let settled;
    if (f.settled !== undefined) {
        const st = f.settled;
        if (typeof st !== 'object' || st === null || !(st.amount === null || isInt(st.amount, 0, 100_000)) || typeof st.auto !== 'boolean')
            return null;
        if (st.leftover !== undefined && (st.leftover !== true || st.amount !== null))
            return null;
        settled = { amount: st.amount, auto: st.auto, ...(st.leftover ? { leftover: true } : {}) };
    }
    const full = f.full === undefined ? undefined : parseFullSwing(f.full);
    if (full === null)
        return null;
    const house = f.house === undefined ? undefined : parseHouse(f.house);
    if (house === null)
        return null;
    // A staged attack (issue 1527) has no defence and no d20 of its own: its throws are its record's.
    const staged = f.staged === undefined ? undefined : parseStagedFacts(f.staged);
    if (staged === null || (staged && (f.ac !== null || rules.critical || house)))
        return null;
    // A spell cast (issue 1530), like a staged attack, has no defence of its own; it is never both.
    const spell = f.spell === undefined ? undefined : parseCastFacts(f.spell);
    if (spell === null || (spell && (staged || f.ac !== null || rules.critical || house)))
        return null;
    if (f.changed !== undefined && !CHANGED_RESULTS.includes(f.changed))
        return null;
    const stops = f.stops === undefined ? undefined : parseDamageStops(f.stops);
    if (stops === null)
        return null;
    if (f.autoCritical !== undefined && !isText(f.autoCritical, ATTACK_LIMITS.name))
        return null;
    return {
        ...(typeof f.autoCritical === 'string' ? { autoCritical: f.autoCritical } : {}), ...(house ? { house } : {}), ...(staged ? { staged } : {}), ...(spell ? { spell } : {}), ...(f.changed !== undefined ? { changed: f.changed } : {}),
        ...(stops ? { stops } : {}), ...(f.scene !== undefined ? { scene: String(f.scene) } : {}), ...(settled ? { settled } : {}), ...(full ? { full } : {}), ...(acTerms && acTerms.length ? { acTerms } : {}),
        ...spec, ac: f.ac, ...(f.note !== undefined ? { note: f.note } : {}), ...(f.confirm !== undefined ? { confirm: f.confirm } : {}),
        ...(f.missRoll !== undefined ? { missRoll: f.missRoll } : {}), ...(f.missAgain !== undefined ? { missAgain: f.missAgain } : {}), ...(f.imageRoll !== undefined ? { imageRoll: f.imageRoll } : {}), ...(f.imageNear !== undefined ? { imageNear: f.imageNear } : {}), ...(damageRoll ? { damageRoll } : {}), attacker, target, rules,
    };
}
/** A stamped swing: exact shape or null. */
export function parseFullSwing(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const x = v;
    if (!UUID_RE.test(String(x.group)) || !isInt(x.of, 1, FULL_SWINGS_MAX) || !isInt(x.swing, 0, x.of - 1))
        return null;
    if (x.from !== undefined && !UUID_RE.test(String(x.from)))
        return null;
    return { group: String(x.group), swing: x.swing, of: x.of, ...(x.from !== undefined ? { from: String(x.from) } : {}) };
}
/**
 * Whether a full attack's swing is set aside for its attacker to send on (qa-runs#1158): its target went down before it
 * landed. A player is told so for their own attack alone, with the swing it is, through `PlayerAttack.leftover`.
 */
export const isLeftover = (f) => !!f?.full && f.settled?.leftover === true;
/** "Down" as the table shows it (qa-runs#1158): dead, or at 0 hit points or fewer. A token with no hit points never is. */
export const isDown = (o) => !!o && (o.dead === true || (typeof o.values?.hp?.current === 'number' && o.values.hp.current <= 0));
const isObjRec = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isChainValue = (v) => typeof v === 'number' || typeof v === 'boolean'
    || (isObjRec(v) && typeof v.bonus === 'number' && Array.isArray(v.dice) && v.dice.every((p) => isObjRec(p) && isInt(p.count, 0, 1000) && isInt(p.sides, 1, 1000)));
const WHO = ['actor', 'dm'];
/** The facts kept on a house record, exactly: a few creatures, each a bounded set of numbers by key. */
function parseHouseFacts(v) {
    if (!isObjRec(v))
        return null;
    const table = (t, nullable) => {
        if (!isObjRec(t) || Object.keys(t).length > 4)
            return null;
        const out = {};
        for (const [id, nums] of Object.entries(t)) {
            if (!isObjRec(nums) || Object.keys(nums).length > 64)
                return null;
            const row = {};
            for (const [k, n] of Object.entries(nums)) {
                if (k.length > 64 || !((typeof n === 'number' && Number.isFinite(n)) || (nullable && n === null)))
                    return null;
                row[k] = n;
            }
            out[id] = row;
        }
        return out;
    };
    const out = {};
    if (v.stats !== undefined) {
        const t = table(v.stats, false);
        if (!t)
            return null;
        out.stats = t;
    }
    if (v.defences !== undefined) {
        const t = table(v.defences, true);
        if (!t)
            return null;
        out.defences = t;
    }
    return out;
}
/** A stored house record (issue 1499, part 3): exact shape or null. The server stamps it; a replay reads it. */
function parseHouse(v) {
    if (!isObjRec(v) || !isText(v.name, 120))
        return null;
    const out = { name: v.name };
    if (v.pending !== undefined) {
        if (!Array.isArray(v.pending) || v.pending.length > ATTACK_LIMITS.terms)
            return null;
        const pending = [];
        for (const p of v.pending) {
            if (!isObjRec(p) || !isText(p.label, ATTACK_LIMITS.termLabel) || typeof p.why !== 'string' || p.why.length > 200)
                return null;
            if (p.aboutTarget !== undefined && p.aboutTarget !== true)
                return null;
            pending.push({ label: p.label, ...(p.aboutTarget ? { aboutTarget: true } : {}), why: p.why });
        }
        if (pending.length)
            out.pending = pending;
    }
    if (v.chain !== undefined) {
        const c = v.chain;
        if (!isObjRec(c) || !Array.isArray(c.blocks) || !c.blocks.length || c.blocks.length > 32)
            return null;
        if (!c.blocks.every((b) => isObjRec(b) && typeof b.id === 'string' && Number.isInteger(b.version) && typeof b.name === 'string' && Array.isArray(b.nodes) && Array.isArray(b.roles)))
            return null;
        if (!isObjRec(c.roles) || !Object.values(c.roles).every((x) => typeof x === 'string'))
            return null;
        if (!(c.rounding === null || c.rounding === 'down' || c.rounding === 'up' || c.rounding === 'half_up'))
            return null;
        if (c.branch !== undefined && !isText(c.branch, ATTACK_LIMITS.ruleWord))
            return null;
        if (!Array.isArray(c.tape) || !c.tape.every((e) => Array.isArray(e) && e.length === 2 && typeof e[0] === 'string' && (e[1] === null || isChainValue(e[1]))))
            return null;
        if (!Array.isArray(c.faces) || !c.faces.every((x) => isInt(x, 1, 1000)))
            return null;
        if (!Array.isArray(c.answers) || !c.answers.every((x) => isText(x, ATTACK_LIMITS.ruleWord)))
            return null;
        if (!Array.isArray(c.ends) || !c.ends.every((e) => isObjRec(e) && typeof e.name === 'string' && (e.outcome === null || typeof e.outcome === 'string') && (e.by === 'rules' || WHO.includes(e.by)) && (e.resolves === undefined || typeof e.resolves === 'string')))
            return null;
        if (c.ask !== undefined && !(isObjRec(c.ask) && WHO.includes(c.ask.who) && typeof c.ask.name === 'string' && Array.isArray(c.ask.outcomes) && c.ask.outcomes.length > 0 && c.ask.outcomes.every((o) => isText(o, ATTACK_LIMITS.ruleWord))))
            return null;
        if (c.stopped !== undefined && typeof c.stopped !== 'string')
            return null;
        out.chain = JSON.parse(JSON.stringify(c));
    }
    if (v.facts !== undefined) {
        const facts = parseHouseFacts(v.facts);
        if (!facts)
            return null;
        out.facts = facts;
    }
    return out;
}
/** An answer's record folded onto the roll as the log holds it (issue 1499): the attack and the numbers it changed. */
export const foldAnswer = (roll, record) => ({ ...roll, modifier: record.modifier, total: record.total, ...(record.attack ? { attack: record.attack } : {}) });
/**
 * The DM's defence for an attack waiting on it (`attack.ac`), folded onto the roll as the log holds it: the record the
 * server stamped when it carries one — a house rule's chain followed from the outcome now known, with whom it asks
 * (issue 1499) — else the defence alone (an older event).
 */
export const foldAc = (roll, body) => body.record?.attack ? foldAnswer(roll, body.record) : roll.attack ? { ...roll, attack: { ...roll.attack, ac: body.ac } } : roll;
/** The person a house rule's chain is asking now (issue 1499, part 3), or null. */
export const houseAsk = (f) => f.house?.chain?.ask ?? null;
/**
 * The outcome of an attack roll (§2.4, §2.5): the roll as it stands — the DM's fudge included (§2.10), whose natural is
 * read back from the new total — against the stamped defence, by the stamped rules. A natural result the system names
 * (3.5: a 20 hits, a 1 misses) decides whatever the total; with no defence known, anything else is unresolved. A
 * threat that hits is confirmed by the confirmation roll, or is a critical at once when the system confirms none.
 */
export function attackOutcome(roll, facts) {
    // A spell cast (issue 1530): it hits when it takes effect.
    if (facts.spell) {
        const o = castOutcome(facts.spell);
        return { result: o.result === 'effect' ? 'hit' : 'miss', natural: o.cast, total: o.cast, threat: false, confirmation: null, concealed: null };
    }
    // A staged attack (issue 1527): waiting for the defender, missed (no successes, dodged, staged below the lowest level), or a hit.
    if (facts.staged) {
        const o = stagedOutcome(facts.staged);
        // In melee (issue 1528) the defender may be the one who hits: the attack itself then missed.
        const result = o.result === 'waiting' ? 'unresolved' : o.result === 'hit' && o.winner !== 'defender' ? 'hit' : 'miss';
        return { result, natural: o.attack, total: o.attack, threat: false, confirmation: null, concealed: null };
    }
    const plain = attackOutcomeOf(roll, facts);
    // A house rule's piece the table did not know waits for the DM's number, as a missing defence does (issue 1499).
    if (facts.house?.pending?.length)
        return { ...plain, result: 'unresolved', threat: false, confirmation: null, concealed: null, ...(facts.rules.tiers ? { tier: null } : {}) };
    // The DM's change of the outcome (workbench §5.2) stands over the rules'.
    if (facts.changed && plain.result !== 'unresolved')
        return { ...plain, result: facts.changed, threat: facts.changed === 'critical', confirmation: null, concealed: null, ...(facts.rules.tiers ? { tier: null } : {}) };
    return plain;
}
/** What a landed attack does with the images around its target: a hit may be on one, a near miss may take one. */
function withImages(o, facts) {
    if (!facts.images || facts.imageRoll === undefined || o.result === 'unresolved' || o.concealed?.missed)
        return o;
    const die = { roll: facts.imageRoll, of: facts.images + 1 };
    if (o.result === 'miss') {
        const ac = facts.ac === null ? null : effectiveAc(facts);
        return facts.imageNear !== undefined && ac !== null && ac - o.total <= facts.imageNear ? { ...o, image: { ...die, destroyed: true, near: true } } : o;
    }
    return facts.imageRoll > 1 ? { ...o, result: 'miss', threat: false, confirmation: null, image: { ...die, destroyed: true }, ...(facts.rules.tiers ? { tier: null } : {}) } : { ...o, image: { ...die, destroyed: false } };
}
function attackOutcomeOf(roll, facts) {
    return withImages(plainOutcome(roll, facts), facts);
}
function plainOutcome(roll, facts) {
    const ac = effectiveAc(facts);
    const struck = rollOutcome(roll, { ...facts, ac });
    // A hit against concealment then rolls its miss chance (§2.6): at or under the chance, it misses, and a threat it
    // turns to a miss confirms nothing.
    if ((struck.result !== 'hit' && struck.result !== 'critical') || !facts.concealment || facts.missRoll === undefined)
        return withTier(struck, facts.rules, ac);
    const first = facts.missRoll <= facts.concealment;
    // Thrown again on that miss, when a feat says so: the second throw stands.
    const again = first ? facts.missAgain : undefined;
    const missed = again === undefined ? first : again <= facts.concealment;
    const concealed = { roll: facts.missRoll, chance: facts.concealment, missed, ...(again !== undefined ? { again } : {}) };
    return missed ? { ...struck, result: 'miss', threat: false, confirmation: null, concealed, ...(facts.rules.tiers ? { tier: null } : {}) } : withTier({ ...struck, concealed }, facts.rules, ac);
}
/**
 * The tier the margin over the defence reaches (issue 1312): the highest whose `min` it reaches, among the tiers on the
 * outcome's side (at or above 0 for a hit, below for a miss). None when a natural result decided it or the defence is
 * not known. Added only when the rules name tiers, so an outcome without them is what it always was.
 */
function withTier(o, rules, ac) {
    if (!rules.tiers)
        return o;
    const decided = rules.natural.some((n) => n.face === o.natural);
    if (o.result === 'unresolved' || ac === null || decided)
        return { ...o, tier: null };
    const margin = o.total - ac;
    const hit = o.result !== 'miss';
    const tier = [...rules.tiers].reverse().find((t) => t.min <= margin && (t.min >= 0) === hit) ?? null;
    return { ...o, tier: tier ? { ...tier } : null };
}
function rollOutcome(roll, facts) {
    const { min, max } = diceRange(facts.rules.dice);
    const total = roll.fudged ? roll.fudged.total : roll.total;
    // A pool's natural result is its kept dice's total or successes (issue 1312); a plain throw's, its faces' sum.
    const face = roll.pool ? poolResult(facts.rules.dice, roll.pool) : roll.dice.reduce((s, f) => s + (typeof f === 'number' ? f : 0), 0);
    const natural = roll.fudged ? Math.min(max, Math.max(min, total - addedModifier(facts.rules.dice, roll.modifier))) : face;
    const versus = (nat, tot) => {
        const named = facts.rules.natural.find((n) => n.face === nat);
        if (named)
            return named.result === 'hit';
        return facts.ac === null ? null : tot >= facts.ac;
    };
    const hit = versus(natural, total);
    if (hit === null)
        return { result: 'unresolved', natural, total, threat: false, confirmation: null, concealed: null };
    if (!hit)
        return { result: 'miss', natural, total, threat: false, confirmation: null, concealed: null };
    // A condition's `auto_critical`, stamped as the attack was made: any hit, with nothing to confirm.
    if (facts.autoCritical)
        return { result: 'critical', natural, total, threat: true, confirmation: null, concealed: null };
    const threat = facts.rules.critical !== null && facts.threat !== undefined && natural >= facts.threat;
    if (!threat)
        return { result: 'hit', natural, total, threat: false, confirmation: null, concealed: null };
    if (facts.confirm === undefined)
        return { result: 'critical', natural, total, threat: true, confirmation: null, concealed: null };
    const confirmTotal = facts.confirm + roll.modifier;
    const confirmed = versus(facts.confirm, confirmTotal);
    return { result: confirmed ? 'critical' : 'hit', natural, total, threat: true, confirmation: { natural: facts.confirm, total: confirmTotal, confirmed }, concealed: null };
}
/**
 * The damage a hit deals (§2.7): every part's dice plus the bonus, at least 1. A critical rolls the weapon's dice the
 * multiplier's number of times and multiplies the bonus; extra dice (fire, sneak attack) are added once. Null for a
 * miss, an attack still waiting for the DM, or one that names no damage.
 */
export function damageOf(facts, outcome) {
    // A spell's damage (issue 1530): the staged level's boxes, of the spell's kind ("6 stun").
    if (facts.spell) {
        const d = castDamage(facts.spell);
        return d ? { parts: [], bonus: 0, total: d.boxes, kind: d.kindLabel.toLowerCase(), critical: false, multiplier: 1 } : null;
    }
    // A staged attack's damage (issue 1527): the staged level's boxes, of the code's kind ("6 physical").
    if (facts.staged) {
        const d = stagedDamage(facts.staged);
        return d ? { parts: [], bonus: 0, total: d.boxes, kind: d.kindLabel.toLowerCase(), critical: false, multiplier: 1 } : null;
    }
    if (!facts.damage || !facts.damageRoll || (outcome.result !== 'hit' && outcome.result !== 'critical'))
        return null;
    const critical = outcome.result === 'critical';
    // `critical.multiplies: "dice"`: every die again, extra dice too, and the bonus once.
    const diceOnly = facts.rules.critical?.multiplies === 'dice';
    const parts = facts.damage.parts.map((p, i) => {
        const faces = [...(facts.damageRoll.faces[i] ?? []), ...(critical && (!p.extra || diceOnly) ? facts.damageRoll.crit[i] ?? [] : [])];
        const label = p.label ?? p.types?.join('/');
        return { count: faces.length, sides: p.sides, ...(label ? { label } : {}), ...(p.types?.length ? { types: p.types } : {}), faces };
    });
    const multiplier = facts.multiplier ?? 1;
    const once = facts.damage.once ?? [];
    const onceBonus = once.reduce((n, t) => n + t.value, 0);
    const multiplied = critical && !diceOnly ? facts.damage.bonus * multiplier : facts.damage.bonus;
    const bonus = multiplied + onceBonus;
    // The margin over the defence or number (issue 1312; Traveller's Effect), where the system adds it: never multiplied.
    const ac = facts.ac === undefined ? null : effectiveAc({ ac: facts.ac, ...(facts.acTerms ? { acTerms: facts.acTerms } : {}) });
    const margin = facts.rules.marginToDamage && ac !== null && outcome.total !== undefined ? Math.max(0, outcome.total - ac) : 0;
    // The system's least damage (3.5: 1; Pathfinder: 1 nonlethal, issue 1502); with none, a hit never deals less than nothing.
    const rolled = parts.reduce((s, p) => s + p.faces.reduce((a, b) => a + b, 0), 0) + bonus + margin;
    const least = leastDamage(rolled, facts.rules.minimumDamage, facts.rules.minimumKind);
    const own = facts.damage.terms?.map((t) => ({ label: t.label, value: critical && !diceOnly ? t.value * multiplier : t.value }));
    const terms = once.length ? [...(own ?? (multiplied ? [{ label: 'base', value: multiplied }] : [])), ...once] : own;
    return { parts, bonus, ...(terms?.length ? { terms } : {}), ...(facts.rules.marginToDamage ? { margin } : {}), total: least.total, ...(least.kind ? { kind: least.kind } : {}), critical, multiplier };
}
/**
 * What a hit deals after what stops it (`damageStops.ts`): `damageOf`'s total when the record carries no stops, else
 * each part's dice as a piece of its own type, the bonus and margin with the first. This is the amount proposed and
 * applied; `damageOf` stays the roll, which is all a player's working ever shows. The lines are the DM's working.
 */
export function damageDealt(facts, outcome) {
    const damage = damageOf(facts, outcome);
    if (!damage)
        return null;
    const s = facts.stops;
    if (!s)
        return { damage, total: damage.total, lines: [] };
    const pieces = damage.parts.map((p) => ({ types: p.types ?? [], amount: p.faces.reduce((a, b) => a + b, 0) }));
    if (!pieces.length)
        return { damage, total: damage.total, lines: [] };
    pieces[0].amount = damage.total - pieces.slice(1).reduce((a, p) => a + p.amount, 0);
    const done = applyStops(s.rules, s.creature, pieces, s.overcome);
    return { damage, total: done.total, lines: done.lines };
}
/**
 * A damage roll's total under the system's least damage (issue 1502): below `minimum`, the minimum, dealt as `kind` when
 * that is not plain damage (Pathfinder: "If penalties reduce the damage result to less than 1, a hit still deals 1 point
 * of nonlethal damage"); with no minimum, never below nothing. The Damage block's judge (chain.ts) reads the same.
 */
export function leastDamage(rolled, minimum, kind) {
    if (minimum === null || minimum === undefined)
        return { total: Math.max(0, rolled) };
    if (rolled >= minimum)
        return { total: rolled };
    return { total: minimum, ...(kind && kind !== PLAIN_DAMAGE ? { kind } : {}) };
}
/**
 * Whether a token is flat-footed by the turn order (§2.2): a fight is running, it is round 1, and the token's
 * combatant has not had its turn yet. Back is not undo, so a stepped-back pointer reads as the turn it points at.
 */
export function flatFooted(enc, tokenId) {
    if (!enc.running || enc.round !== 1)
        return false;
    const i = enc.combatants.findIndex((c) => c.tokens.includes(tokenId));
    return i > enc.activeIndex;
}
/** The names a player may be told: a released layer's. */
const shownFrom = (from) => {
    const out = {};
    for (const k of RULING_PARTS)
        if (from?.[k]?.shown)
            out[k] = from[k].name;
    return Object.keys(out).length ? out : undefined;
};
const sees = (side, viewerId) => side.seen || (side.seenBy?.includes(viewerId) ?? false) || (side.ownersSee !== false && side.owners.includes(viewerId));
const nameFor = (side, viewerId) => (side.named || side.owners.includes(viewerId) ? side.name : null);
/**
 * A player's version of an attack roll, or null when nothing of it reaches them: an attack on a target they cannot see
 * is not there for them (design/22). The record is the stored one; the outcome is worked out from it here, on the
 * server, for this viewer — a fudge moves the outcome, and never shows its numbers.
 */
export function playerAttack(roll, viewerId) {
    const facts = roll.attack;
    if (!facts || !sees(facts.target, viewerId))
        return null;
    const outcome = attackOutcome(roll, facts);
    // A player's attack is a player's roll, public like every one of theirs; the DM's, public or not, never shows its bonus.
    // Its working — the terms, its conditions and effects among them, the miss chance and the damage — is for the
    // attacker's own players (design/29 §6); everyone else is told the total and the outcome (the review of 2026-10-07:
    // another player's working showed conditions and effect numbers its owner does not share).
    const playerRoll = !roll.private && facts.attacker.owners.includes(roll.actor.id);
    const ownWorking = playerRoll && facts.attacker.owners.includes(viewerId);
    const shown = !roll.private;
    // A house rule's secret piece (issue 1499): the working says a situation counted, never its number, and no player is
    // given a total it would be the difference of — as no player is given the defence an outcome was judged against.
    const secret = facts.terms.some((t) => t.secret);
    if (facts.spell) {
        return {
            id: roll.id, at: roll.at, actor: { ...roll.actor },
            attacker: sees(facts.attacker, viewerId) ? { name: nameFor(facts.attacker, viewerId) } : null,
            target: { name: nameFor(facts.target, viewerId) },
            name: shown ? facts.name : null, result: outcome.result, total: null, working: null,
            spell: playerCast(facts.spell, roll, { attacker: facts.attacker, target: facts.target }, viewerId),
            ruling: { dice: copyDice(facts.rules.dice), words: { ...facts.rules.words } },
        };
    }
    if (facts.staged) {
        return {
            id: roll.id, at: roll.at, actor: { ...roll.actor },
            attacker: sees(facts.attacker, viewerId) ? { name: nameFor(facts.attacker, viewerId) } : null,
            target: { name: nameFor(facts.target, viewerId) },
            name: shown ? facts.name : null, result: outcome.result, total: shown ? outcome.total : null, working: null,
            staged: playerStaged(facts.staged, roll, { attacker: facts.attacker, target: facts.target }, viewerId),
            ruling: { dice: copyDice(facts.rules.dice), words: { ...facts.rules.words } },
        };
    }
    return {
        id: roll.id,
        at: roll.at,
        actor: { ...roll.actor },
        attacker: sees(facts.attacker, viewerId) ? { name: nameFor(facts.attacker, viewerId) } : null,
        target: { name: nameFor(facts.target, viewerId) },
        name: shown ? facts.name : null,
        result: outcome.result,
        total: shown && !secret ? outcome.total : null,
        // The miss chance and the damage dice ride in the working alone: a player's own attack shows them; the DM's damage
        // reaches a player only as hit points, once applied (#1125).
        working: ownWorking ? {
            natural: outcome.natural, terms: facts.terms.filter((t) => !t.secret).map((t) => (t.aboutTarget ? { label: 'situation', value: t.value } : { label: t.label, value: t.value })),
            ...(facts.acTerms?.some((t) => t.shown) ? { against: facts.acTerms.filter((t) => t.shown).map((t) => ({ label: t.label, value: t.value })) } : {}),
            ...(secret ? { situation: true } : {}),
            confirmation: outcome.confirmation && secret ? { ...outcome.confirmation, total: null } : outcome.confirmation, concealed: outcome.concealed, ...(outcome.image ? { image: outcome.image } : {}), damage: damageOf(facts, outcome),
            // What stopped it reaches the attacker only when the DM told the players what this creature stops (`told`).
            ...(facts.stops?.told ? { stopped: damageDealt(facts, outcome)?.lines.map(stopLineText) ?? [] } : {}),
            // A player's own roll is never fudged (only the DM's private ones are), so its dice are what made its result.
            ...(roll.pool && !roll.fudged ? { pool: roll.pool.map((x) => ({ ...x, faces: [...x.faces] })) } : {}),
            ...(roll.advantage ? { advantage: advantageText(roll.advantage, true) } : {}),
        } : null,
        ...(outcome.tier ? { tier: outcome.tier.label } : {}),
        // The person a house rule asks (issue 1499): the acting player gets the buttons; everyone else whom it waits for.
        // Never the block's words for anyone but the asked player, and never what the DM is asked.
        ...askFor(roll, viewerId),
        ...(ownWorking && facts.full ? { full: { group: facts.full.group, swing: facts.full.swing, of: facts.full.of, token: facts.attacker.id, ...(facts.ranged ? { ranged: true } : {}) }, ...(isLeftover(facts) ? { leftover: true } : {}) } : {}),
        // A private roll tells a player nothing of how it was thrown: the dice as they would be with neither.
        ruling: { dice: roll.private && roll.advantage?.result ? { count: facts.rules.dice.keep.n, sides: facts.rules.dice.sides } : copyDice(facts.rules.dice), words: { ...facts.rules.words }, ...(ownWorking ? namedFor(shownFrom(facts.rules.from), facts.rules.natural, outcome) : {}), ...(ownWorking && facts.rules.borrowed ? { borrowed: facts.rules.borrowed } : {}) },
    };
}
function askFor(roll, viewerId) {
    const ask = roll.attack ? houseAsk(roll.attack) : null;
    if (!ask)
        return {};
    if (ask.who === 'actor' && roll.actor.id === viewerId && !roll.by)
        return { ask: { name: ask.name, outcomes: [...ask.outcomes] } };
    return { waiting: ask.who === 'dm' ? 'the DM' : roll.actor.handle ?? 'the player' };
}
export function parsePlayerAttack(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const a = v;
    if (typeof a.id !== 'string' || typeof a.at !== 'string' || typeof a.result !== 'string' || !a.target || !a.actor || !a.ruling?.dice || !a.ruling.words)
        return null;
    return a;
}
// ── The lines (§2, §6) ────────────────────────────────────────────────────
const signed = (n) => (n < 0 ? `− ${-n}` : `+ ${n}`);
/** A result in the system's words; "waiting for the DM" is the table's. */
const resultWord = (r, words) => (r === 'unresolved' ? 'waiting for the DM' : words[r]);
/**
 * The working of a roll: "d20 (14) + BAB 1 + Str 2 = 17", with the system's dice. With a pool's dice (issue 1312) each
 * die is shown: "5d10, exploding on 10, successes on 8+ (8, 3, 10→6, 9, 2): 3 successes + 1 skill = 4"; a pool whose
 * modifiers are dice shows the dice they made: "5d10 … + 1 skill = 6 dice (…): 3 successes".
 */
export function workingLine(natural, terms, total, dice, pool, situation) {
    // A secret piece (issue 1499): "+ situation" with no number, and no total.
    const termText = terms.map((t) => ` ${signed(t.value)} ${t.label}`).join('') + (situation ? ' + situation' : '');
    const eq = total === null ? '' : ` = ${total}`;
    if (!pool)
        return `${diceText(dice)} (${natural})${termText}${eq}`;
    const count = dice.success ? ` ${natural === 1 ? 'success' : 'successes'}` : '';
    if (dice.success?.modifiers === 'dice')
        return `${diceText(dice)}${termText} = ${pool.length} ${pool.length === 1 ? 'die' : 'dice'} (${poolText(pool)}): ${natural}${count}`;
    return `${diceText(dice)} (${poolText(pool)}): ${natural}${count}${termText}${eq}`;
}
/** A result in its tier's words when one was reached (issue 1312), else the system's. */
const outcomeWord = (r, words, tier) => (tier ?? resultWord(r, words));
const imageLine = (i) => `images d${i.of} (${i.roll}): ${i.near ? 'a near miss takes one' : i.destroyed ? 'it struck an image, which goes' : 'it struck the creature'}`;
const concealedLine = (c) => `miss chance d100 (${c.again !== undefined ? `${c.roll}, again ${c.again}` : c.roll}) ${c.missed ? '≤' : '>'} ${c.chance}%: ${c.missed ? 'miss' : 'it hits'}`;
/**
 * "damage: 2d6 (4, 5) + 3 = 12"; on a critical "damage ×2: 4d6 (4, 5, 1, 6) + 6 = 22", and with a homebrew layer's
 * multiplier "damage ×3 (homebrew: Brutal Crits): …" (issue 1310).
 */
export function damageLine(d, criticalBy) {
    const dice = d.parts.map((p) => `${p.count}d${p.sides}${p.label ? ` ${p.label}` : ''} (${p.faces.join(', ')})`).join(' + ');
    // Its bonus line by line when it carries them (issue 1506): "+ 3 base + 2 Weapon Specialization (longsword)".
    const bonus = d.terms?.length ? d.terms.map((t) => ` ${signed(t.value)} ${t.label}`).join('') : d.bonus ? ` ${signed(d.bonus)}` : '';
    return `damage${d.critical ? ` ×${d.multiplier}${criticalBy ? ` ${byText([criticalBy])}` : ''}` : ''}: ${dice}${bonus}${d.margin ? ` + ${d.margin} margin` : ''} = ${d.total}${d.kind ? ` ${d.kind}` : ''}`;
}
/** "(homebrew: Brutal Crits)", each layer once; a DM's ruling (issue 1136) by its own name, "(ruling: Crits ×3 tonight)". */
const byText = (names) => {
    const all = [...new Set(names)];
    const layers = all.filter((n) => !n.startsWith('ruling: '));
    return `(${[...(layers.length ? [`homebrew: ${layers.join(', ')}`] : []), ...all.filter((n) => n.startsWith('ruling: '))].join('; ')})`;
};
/**
 * The layers whose rules decided this result (issue 1310), by name, from the names the viewer may read: the dice always,
 * a natural result when the roll showed a face the rules name, the critical when it was one.
 */
function decidedBy(names, natural, outcome) {
    if (!names)
        return [];
    const by = [];
    if (names.dice)
        by.push(names.dice);
    if (names.natural && natural.some((n) => n.face === outcome.natural))
        by.push(names.natural);
    if (names.critical && (outcome.result === 'critical' || outcome.threat))
        by.push(names.critical);
    return [...new Set(by)];
}
const byTail = (by) => (by && by.length ? ` ${byText(by)}` : '');
/** " (D&D 3.5 rules)": the system the table borrowed its rules from (issue 1305), or nothing. */
export const borrowedTail = (name) => (name ? ` (${name} rules)` : '');
/** What a player's frame carries of the layers (issue 1310): the names that decided it and the critical's, or nothing. */
function namedFor(names, natural, outcome) {
    const by = decidedBy(names, natural, outcome);
    const criticalBy = outcome.result === 'critical' ? names?.critical : undefined;
    return { ...(by.length ? { by } : {}), ...(criticalBy ? { criticalBy } : {}) };
}
const allNames = (from) => {
    if (!from)
        return undefined;
    const out = {};
    for (const k of RULING_PARTS)
        if (from[k])
            out[k] = from[k].name;
    return out;
};
/** The DM's whole line for an attack roll: who, against whom, the working, the defence, and the outcome. */
export function dmAttackLines(roll) {
    const f = roll.attack;
    if (!f)
        return [];
    if (f.spell) {
        const dmg = damageOf(f, attackOutcome(roll, f));
        const settled = dmg && f.settled ? [f.settled.amount === null ? 'damage dropped' : `${f.settled.auto ? 'applied at once' : 'applied'}: ${f.settled.amount} ${dmg.kind ?? 'damage'}`] : [];
        return [`${f.attacker.name} → ${f.target.name}: ${f.name}`, ...castLines(f.spell), ...settled];
    }
    if (f.staged) {
        const dmg = damageOf(f, attackOutcome(roll, f));
        const settled = dmg && f.settled ? [f.settled.amount === null ? 'damage dropped' : `${f.settled.auto ? 'applied at once' : 'applied'}: ${f.settled.amount} ${dmg.kind ?? 'damage'}`] : [];
        return [`${f.attacker.name} → ${f.target.name}: ${f.name}`, ...stagedLines(f.staged), ...settled];
    }
    const o = attackOutcome(roll, f);
    const changes = f.acTerms ?? [];
    const label = f.rules.defence;
    const versus = f.ac === null ? `no ${label} known`
        : changes.length ? `${label} ${f.ac}${changes.map((t) => ` ${signed(t.value)} ${t.label}`).join('')} = ${effectiveAc(f)}` : `${label} ${f.ac}`;
    // The roll's own verdict: a hit the miss chance then turned to a miss still hit the defence.
    const struck = o.concealed?.missed || (o.image?.destroyed && !o.image.near) ? f.rules.words.hit : outcomeWord(o.result, f.rules.words, o.tier?.label);
    // The DM is told every layer that decided it, released or not: it is their own table's homebrew (issue 1310).
    const names = allNames(f.rules.from);
    const lines = [`${f.attacker.name} → ${f.target.name}: ${f.name}`, `${workingLine(o.natural, f.terms, o.total, f.rules.dice, roll.fudged ? undefined : roll.pool)} vs ${versus}: ${struck}${byTail(decidedBy(names, f.rules.natural, o))}${borrowedTail(f.rules.borrowed)}`];
    if (roll.advantage)
        lines.push(advantageText(roll.advantage));
    if (f.note)
        lines.push(f.note);
    if (o.confirmation)
        lines.push(`${f.rules.words.threat} — confirm ${workingLine(o.confirmation.natural, f.terms, o.confirmation.total, f.rules.dice)}: ${o.confirmation.confirmed ? 'confirmed' : o.confirmation.confirmed === null ? 'waiting for the DM' : 'not confirmed'}`);
    if (o.concealed)
        lines.push(concealedLine(o.concealed));
    if (o.image)
        lines.push(imageLine(o.image));
    const dmg = damageOf(f, o);
    if (dmg)
        lines.push(damageLine(dmg, names?.critical));
    // What stopped it (the DM's working): one line a step, and the total they come to.
    const dealt = damageDealt(f, o);
    if (dealt?.lines.length)
        lines.push(...dealt.lines.map(stopLineText), `deals ${dealt.total}`);
    // Nonlethal (issue 1502) reads as what it is, "1 nonlethal": it went on the count, not off the hit points.
    if (dmg && f.settled)
        lines.push(f.settled.amount === null ? 'damage dropped' : `${f.settled.auto ? 'applied at once' : 'applied'}: ${f.settled.amount} ${dmg.kind === 'nonlethal' ? 'nonlethal' : 'damage'}`);
    lines.push(...houseLines(roll));
    return lines;
}
/** The DM's lines for a house rule's part in an attack (issue 1499, part 3): what waits, the chain after it, a change. */
export function houseLines(roll) {
    const f = roll.attack;
    if (!f)
        return [];
    const out = [];
    if (f.changed)
        out.push(`outcome changed by the DM: ${f.rules.words[f.changed]}`);
    for (const p of f.house?.pending ?? [])
        out.push(`${p.label}: ${p.why}; waiting for your number`);
    const c = f.house?.chain;
    if (!c)
        return out;
    const whoName = (who) => (who === 'dm' ? 'the DM' : who === 'actor' ? roll.actor.handle ?? 'the player' : 'the rules');
    for (const e of c.ends)
        out.push(`${e.name}: ${e.outcome ?? 'done'}${e.by !== 'rules' ? ` (${whoName(e.by)})` : ''}${e.resolves ? ` → ${e.resolves}` : ''}`);
    if (c.ask)
        out.push(`${c.ask.name}: waiting for ${whoName(c.ask.who)} (${c.ask.outcomes.join(' / ')})`);
    if (c.stopped)
        out.push(`${f.house.name} stopped: ${c.stopped}`);
    return out;
}
/** A player's line: "The ogre hits Shelby", with the total and the working where they may see them. */
export function playerAttackLines(a) {
    const who = a.attacker ? (a.attacker.name ?? 'A creature') : null;
    const whom = a.target.name ?? 'a creature';
    const verb = a.result === 'miss' ? 'misses' : a.result === 'critical' ? 'critically hits' : a.result === 'hit' ? 'hits' : 'attacks';
    if (a.spell) {
        const top = who ? `${who} casts ${a.name ?? 'a spell'} at ${whom}` : `${whom[0].toUpperCase()}${whom.slice(1)} is the target of a spell${a.name ? ` — ${a.name}` : ''}`;
        return [top, ...a.spell.lines];
    }
    if (a.staged) {
        const s = a.staged;
        const what = a.result === 'miss' ? 'misses' : a.result === 'hit' ? 'hits' : 'attacks';
        // Melee (issue 1528): both sides fight, and the lines say who hits.
        const top = s.melee ? (who ? `${who} attacks ${whom} in melee` : `${whom[0].toUpperCase()}${whom.slice(1)} is attacked in melee`)
            : who ? `${who} ${what} ${whom}` : `${whom[0].toUpperCase()}${whom.slice(1)} ${a.result === 'miss' ? 'is missed' : a.result === 'hit' ? 'is hit' : 'is attacked'}`;
        // The defender's question is the buttons' own words (StagedAsk), so it is not a line too.
        return [`${top}${a.name ? ` — ${a.name}` : ''}`, ...s.lines, ...(s.waiting ? [`Waiting for ${s.waiting}`] : [])];
    }
    const head = who ? `${who} ${verb} ${whom}` : a.result === 'miss' ? `An attack misses ${whom}` : a.result === 'unresolved' ? `${whom[0].toUpperCase()}${whom.slice(1)} is attacked` : `${whom[0].toUpperCase()}${whom.slice(1)} is ${a.result === 'critical' ? 'critically hit' : 'hit'}`;
    const lines = [`${head}${a.name ? ` — ${a.name}` : ''}${a.total !== null && !a.working ? `: ${a.total}` : ''}${a.tier ? ` (${a.tier})` : ''}`];
    if (a.working && (a.total !== null || a.working.situation)) {
        // The homebrew that decided it, as far as this player may be told (issue 1310): released layers only.
        lines.push(`${workingLine(a.working.natural, a.working.terms, a.total, a.ruling.dice, a.working.pool, a.working.situation)}: ${a.working.concealed?.missed || (a.working.image?.destroyed && !a.working.image.near) ? a.ruling.words.hit : outcomeWord(a.result, a.ruling.words, a.tier)}${byTail(a.ruling.by)}${borrowedTail(a.ruling.borrowed)}`);
        if (a.working.advantage)
            lines.push(a.working.advantage);
        const c = a.working.confirmation;
        if (c)
            lines.push(`${a.ruling.words.threat} — confirm ${workingLine(c.natural, a.working.terms, c.total, a.ruling.dice, undefined, a.working.situation)}: ${c.confirmed ? 'confirmed' : c.confirmed === null ? 'waiting for the DM' : 'not confirmed'}`);
        if (a.working.against?.length)
            lines.push(`${whom[0].toUpperCase()}${whom.slice(1)} has ${a.working.against.map((t) => `${t.label} ${signed(t.value)}`).join(', ')}`);
        if (a.working.concealed)
            lines.push(concealedLine(a.working.concealed));
        if (a.working.image)
            lines.push(imageLine(a.working.image));
        if (a.working.damage)
            lines.push(damageLine(a.working.damage, a.ruling.criticalBy));
        if (a.working.stopped)
            lines.push(...a.working.stopped);
    }
    else if (a.result === 'unresolved')
        lines.push('Waiting for the DM');
    if (a.ask)
        lines.push(`${a.ask.name}: your choice`);
    else if (a.waiting && a.result !== 'unresolved')
        lines.push(`Waiting for ${a.waiting}`);
    return lines;
}
const STAT_ATTACKS_MAX = 40;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** "2d8+7", "1d6", "3": one damage of the data's shape (`{ dice, bonus, types? }`) in words; null when it is not one. */
function damageWords(d) {
    if (!isObj(d) || !Array.isArray(d.dice) || !isInt(d.bonus, -1000, 1000))
        return null;
    const dice = [];
    for (const x of d.dice) {
        if (!isObj(x) || !isInt(x.count, 1, 100) || !isInt(x.sides, 1, 1000))
            return null;
        dice.push(`${x.count}d${x.sides}`);
    }
    const types = Array.isArray(d.types) ? d.types.filter((t) => typeof t === 'string' && /^[a-z][a-z ]{0,30}$/i.test(t)) : [];
    const head = dice.join('+') + (d.bonus ? `${d.bonus > 0 && dice.length ? '+' : ''}${d.bonus}` : dice.length ? '' : '0');
    return `${head}${types.length ? ` ${types.join(' ')}` : ''}`;
}
/**
 * A creature's full attacks (qa-runs#1158): each option of its `full` (*greatclub … or javelin …* is two), every attack
 * in it read as `statAttacksFrom` reads one. An option with nothing that holds the shape is left out.
 */
export function fullAttackOptions(value) {
    if (!isObj(value) || !Array.isArray(value.full))
        return [];
    return value.full.filter(Array.isArray).map((option) => statAttacksFrom({ full: [option] })).filter((o) => o.length);
}
/** A full attack's swings from a stat block's option (qa-runs#1158): each attack times its count at each of its bonuses, in order. */
export function statFullSwings(option, defaults) {
    return option.flatMap((a) => a.bonuses.flatMap((_, k) => Array.from({ length: a.count }, () => specOfStatAttack(a, defaults, k)))).slice(0, FULL_SWINGS_MAX);
}
/** How many attacks a base attack bonus gives (both systems: one more at +6, +11 and +16). */
export const iterativesOf = (bab) => (bab >= 16 ? 4 : bab >= 11 ? 3 : bab >= 6 ? 2 : 1);
/**
 * A character's full attack (qa-runs#1158): the weapon at each iterative bonus of the sheet's base attack bonus, each
 * 5 lower than the one before ("attack 2 −5"), then the off-hand weapon's one swing at its own sheet bonus.
 */
export function sheetFullSwings(main, bab, offHand) {
    const n = iterativesOf(bab);
    const swings = Array.from({ length: n }, (_, i) => (i === 0 ? main : { ...main, terms: [...main.terms, { label: `attack ${i + 1}`, value: -5 * i }].slice(-ATTACK_LIMITS.terms) }));
    return offHand ? [...swings, offHand] : swings;
}
/**
 * A full attack with the swings the attacker's entries add (qa-runs#1159; docs/MRCS/feats.md): each entry it carries
 * (`carries`), or, `byWeapon`, the first swing's weapon carries as a special ability of the entry's name (a speed
 * weapon: its `abilities` hold `speed`; never a word in the weapon's name), adds one swing per `at`:
 * an off-hand one a copy of the off-hand swing, after the others (none without one); any other a copy of the first
 * swing, after the main hand's. One entry of a `group` only (haste and a speed weapon do not stack). Each added swing
 * has the entry's name as a line, with its number. `added`: the names of the entries that added swings.
 */
export function withAddedSwings(swings, adds, carries) {
    const first = swings.find((s) => !s.offHand) ?? swings[0];
    const off = [...swings].reverse().find((s) => s.offHand);
    if (!first)
        return { swings, added: [] };
    const slugOf = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const hasAbility = (s, name) => (s.abilities ?? []).includes(slugOf(name));
    const groups = new Set();
    const main = [];
    const offs = [];
    const added = [];
    for (const a of adds) {
        if (!(a.byWeapon ? hasAbility(first, a.name) : carries(a)))
            continue;
        if (a.group && groups.has(a.group))
            continue;
        const base = a.hand === 'off' ? off : first;
        if (!base)
            continue;
        if (a.group)
            groups.add(a.group);
        added.push(a.name);
        for (const at of a.at) {
            const spec = { ...base, terms: [...base.terms, { label: a.name, value: at }].slice(-ATTACK_LIMITS.terms) };
            if (a.hand === 'off')
                offs.push({ at, spec });
            else
                main.push(spec);
        }
    }
    if (!added.length)
        return { swings, added };
    const cut = swings.findIndex((s) => s.offHand);
    const head = cut < 0 ? swings : swings.slice(0, cut);
    const tail = cut < 0 ? [] : swings.slice(cut);
    return { swings: [...head, ...main, ...tail, ...offs.sort((x, y) => y.at - x.at).map((o) => o.spec)].slice(0, FULL_SWINGS_MAX), added };
}
/** The row an option's number reaches (qa-runs#1159): the last whose `from` it reaches; null below the first. */
export function optionRow(o, n) {
    if (!o.rows?.length)
        return {};
    if (n === null)
        return null;
    return [...o.rows].sort((a, b) => a.from - b.from).filter((r) => r.from <= n).pop() ?? null;
}
const withTerm = (s, label, value) => (value ? { ...s, terms: [...s.terms, { label, value }].slice(-ATTACK_LIMITS.terms) } : s);
/**
 * One arrow after the first of a Manyshot (qa-runs#1159): the swing's spec with no critical (a threat no natural roll
 * reaches) and its damage's extra dice left off (precision damage, sneak attack, counts on the first arrow only).
 */
export function arrowSpec(s) {
    const parts = s.damage?.parts.filter((p) => !p.extra) ?? [];
    const { multiplier: _m, ...rest } = s;
    return { ...rest, threat: ATTACK_LIMITS.natural, ...(s.damage ? (parts.length ? { damage: { ...s.damage, parts } } : { damage: { ...s.damage, parts: [{ count: 1, sides: 1 }] } }) : {}) };
}
/**
 * The swings an option makes (qa-runs#1159), from the request's swings (a full attack's, or the one attack) and the
 * option's row. `foes`: each standing enemy next to the attacker, for `each_foe`. Arrows and flurry put their term
 * (*Manyshot −6*, *Flurry of Blows −2*) on every swing they change.
 */
export function optionSwings(o, row, swings, foes) {
    const first = swings[0];
    if (!first)
        return [];
    if (o.shape === 'each_foe')
        return foes.map((id) => ({ attack: first, target: id }));
    const pen = row.penalty ?? 0;
    if (o.shape === 'arrows') {
        const head = withTerm(first, o.name, pen);
        const arrows = Array.from({ length: Math.max(1, row.count ?? 2) - 1 }, () => ({ attack: arrowSpec(head), arrow: true }));
        return [{ attack: head }, ...arrows, ...(o.action === 'full' ? swings.slice(1).map((s) => ({ attack: withTerm(s, o.name, pen) })) : [])];
    }
    const all = swings.map((s) => withTerm(s, o.name, pen));
    const cut = all.findIndex((s) => s.offHand);
    const main = cut < 0 ? all : all.slice(0, cut);
    const tail = cut < 0 ? [] : all.slice(cut);
    if (row.swings)
        return [...row.swings].sort((a, b) => b - a).map((n) => withTerm(main[0], `${o.name} swing`, n)).concat(tail).slice(0, FULL_SWINGS_MAX).map((attack) => ({ attack }));
    const at = [...(row.at ?? [0])].sort((a, b) => b - a);
    const copies = at.map((n) => withTerm(main[0], `${o.name} swing`, n));
    return [main[0], ...copies.filter((_, i) => at[i] === 0), ...main.slice(1), ...copies.filter((_, i) => at[i] !== 0), ...tail].slice(0, FULL_SWINGS_MAX).map((attack) => ({ attack }));
}
/** The save options of a creature's `attacks` register, each once. `statAttacksFrom` leaves them out: they have no bonus. */
export function saveOptionsFrom(value) {
    if (!isObj(value))
        return [];
    const out = [];
    for (const part of ['full', 'single']) {
        const options = value[part];
        for (const option of Array.isArray(options) ? options : [])
            for (const a of Array.isArray(option) ? option : []) {
                if (!isObj(a) || !isObj(a.save) || typeof a.name !== 'string' || !a.name.trim())
                    continue;
                const name = a.name.trim().slice(0, ATTACK_LIMITS.name);
                if (out.some((o) => o.name === name))
                    continue;
                const s = a.save;
                const r = a.recharge;
                if (typeof s.key !== 'string' || !isInt(s.dc, 1, 100) || (s.on_save !== 'half' && s.on_save !== 'none'))
                    continue;
                const area = (s.shape === 'cone' || s.shape === 'line') && isInt(s.feet, 5, 1000) ? { shape: s.shape, feet: s.feet } : {};
                out.push({ name, save: s.key, dc: s.dc, onSave: s.on_save, damage: damageWords(s.damage), ...area, ...(isObj(r) && isInt(r.sides, 2, 100) && isInt(r.from, 1, r.sides) ? { recharge: { from: r.from, sides: r.sides } } : {}) });
            }
    }
    return out;
}
/**
 * A creature's attacks out of the value of the attribute that tags the `attacks` register (issue 1308; the shape is
 * docs/MRCS/monster-attacks.md, issue 1217): every attack of its full attack's options, then of its single attack's,
 * each once, in order. Read by the register, whatever the system calls the attribute, and never from stat-block text.
 * An automatic attack (a swarm's) makes no attack roll, so it is not one to fire; an attack that does not hold the
 * shape is left out, never guessed (§4).
 */
export function statAttacksFrom(value) {
    if (!isObj(value))
        return [];
    const out = [];
    const seen = new Set();
    for (const part of ['full', 'single']) {
        const options = value[part];
        if (!Array.isArray(options))
            continue;
        for (const option of options) {
            if (!Array.isArray(option))
                continue;
            for (const a of option) {
                if (!isObj(a) || a.automatic === true || typeof a.name !== 'string' || !a.name.trim())
                    continue;
                if (a.reach !== 'melee' && a.reach !== 'ranged')
                    continue;
                if (!Array.isArray(a.bonuses) || !a.bonuses.length || !a.bonuses.every((b) => isInt(b, -1000, 1000)))
                    continue;
                const name = a.name.trim().replace(/\s+/g, ' ').slice(0, ATTACK_LIMITS.name);
                const bonuses = a.bonuses;
                const main = damageWords(a.damage);
                const extra = Array.isArray(a.extra_damage) ? a.extra_damage.map(damageWords).filter((w) => w !== null) : [];
                // The same attack in the single and the full attack is listed once; a pair of weapons with a damage each
                // ("2 daggers (1d6+2, 1d6+1)") is two.
                const key = `${name.toLowerCase()} ${bonuses.join('/')} ${a.reach} ${[main, ...extra].join(' plus ')}`;
                if (seen.has(key))
                    continue;
                seen.add(key);
                const c = isObj(a.critical) ? a.critical : null;
                out.push({
                    name: name[0].toUpperCase() + name.slice(1), count: isInt(a.count, 1, 100) ? a.count : 1, bonuses, ranged: a.reach === 'ranged', touch: a.touch === true,
                    damage: main ? [main, ...extra].join(' plus ') : null,
                    ...(c && isInt(c.threat, 1, ATTACK_LIMITS.natural) ? { threat: c.threat } : {}),
                    ...(c && isInt(c.multiplier, 2, ATTACK_LIMITS.multiplier) ? { multiplier: c.multiplier } : {}),
                });
                if (out.length >= STAT_ATTACKS_MAX)
                    return out;
            }
        }
    }
    return out;
}
/**
 * A critical as a sheet or an item writes it (§2.5): "19-20/x2", "x3", "18–20/×2". Only what it says: a part it does
 * not say (or says out of range) is left out, and the attack is made at the system's own when it is committed.
 */
export function parseCritical(text) {
    const t = String(text ?? '');
    const range = /(\d+)\s*[-–]\s*(\d+)/.exec(t);
    const mult = /[x×]\s*(\d+)/i.exec(t);
    const threat = range ? Number(range[1]) : NaN;
    const multiplier = mult ? Number(mult[1]) : NaN;
    return {
        ...(isInt(threat, 1, ATTACK_LIMITS.natural) ? { threat } : {}),
        ...(isInt(multiplier, 2, ATTACK_LIMITS.multiplier) ? { multiplier } : {}),
    };
}
export function attackDefaults(combat) {
    // An attack against the system's fixed number (issue 1312) has no defence to name: a sheet's weapon and a stat
    // block's attack are made against that number (issue 1137: they were offered none).
    if (combat?.attack?.against === 'number') {
        const c = combat.attack.critical;
        return { main: 'number', touch: 'number', critical: c ? { threat: c.threat, multiplier: c.multiplier } : null };
    }
    const main = combat ? mainDefence(combat) : null;
    if (!main)
        return null;
    const c = combat.attack?.critical;
    return { main: main.key, touch: combat.defences.find((d) => d.key === 'touch')?.key ?? main.key, critical: c ? { threat: c.threat, multiplier: c.multiplier } : null };
}
/** A sheet bonus's lines (issue 1137): its itemised terms when they are whole, in bounds and add up to it; else the one bonus. */
function sheetTerms(a) {
    const t = a.terms;
    // The base and up to `terms - 4` modifiers (issue 1506), leaving room for what the table adds.
    const fits = Array.isArray(t) && t.length > 1 && t.length <= ATTACK_LIMITS.terms - 3
        && t.every((x) => isObj(x) && isText(x.label, ATTACK_LIMITS.termLabel) && isInt(x.value, -ATTACK_LIMITS.termValue, ATTACK_LIMITS.termValue))
        && bonusOf(t) === a.bonus;
    return fits ? t.map((x) => ({ label: x.label.trim(), value: x.value })) : [{ label: 'attack', value: a.bonus }];
}
/** A sheet's weapon as the attack it fires: the sheet's own bonus, its damage and critical, melee or ranged, against the system's main defence. */
export function specOfSheetAttack(a, defaults) {
    const dice = a.damage ? parseDamageText(a.damage) : null;
    // Its bonus line by line (issue 1506) when the lines add up to it; else the one bonus.
    const lines = dice && a.damageTerms ? damageTermsOf(a.damageTerms, dice.bonus) : null;
    const damage = dice && lines ? { ...dice, terms: lines } : dice;
    return {
        name: a.name.slice(0, ATTACK_LIMITS.name) || 'Attack', terms: sheetTerms(a), ...(defaults.critical ? { ...defaults.critical, ...parseCritical(a.critical) } : {}), defence: defaults.main,
        ...(damage ? { damage } : {}), ...(a.ranged ? { ranged: true } : {}), ...(a.twoHanded ? { twoHanded: true } : {}), ...(a.offHand ? { offHand: true } : {}), ...(a.row ? { sheetRow: a.row } : {}),
        ...(a.abilities?.length ? { abilities: a.abilities.slice(0, ABILITY_LIMITS.count) } : {}),
    };
}
/** The sheet row an attack from the wire names (issue 1481): up to 64 characters, none a control character; else null. Untrusted. */
export function sheetRowOf(attack) {
    const r = typeof attack === 'object' && attack !== null ? attack.sheetRow : undefined;
    // eslint-disable-next-line no-control-regex
    return typeof r === 'string' && /^[^\u0000-\u001f\u007f]{1,64}$/.test(r) ? r : null;
}
/** A stat block's attack as the attack it fires: its first (highest) bonus, the touch defence when it is a touch attack. */
export function specOfStatAttack(a, defaults, iterative = 0) {
    const bonus = a.bonuses[Math.min(iterative, a.bonuses.length - 1)] ?? 0;
    const damage = a.damage ? parseDamageText(a.damage) : null;
    return {
        name: a.name, terms: [{ label: 'attack', value: bonus }],
        ...(defaults.critical ? { threat: a.threat ?? defaults.critical.threat, multiplier: a.multiplier ?? defaults.critical.multiplier } : {}),
        defence: a.touch ? defaults.touch : defaults.main, ...(damage ? { damage } : {}), ...(a.ranged ? { ranged: true } : {}),
    };
}
/** A throw copied whole, its parts with it (issue 1312), so a stamped record shares nothing with the rules. */
export const copyDice = (d) => ({
    count: d.count, sides: d.sides, ...(d.explode ? { explode: { ...d.explode } } : {}), ...(d.keep ? { keep: { ...d.keep } } : {}), ...(d.success ? { success: { ...d.success } } : {}),
});
/**
 * The ruling an attack against `defence` is made by, out of the system's combat rules (issue 1303): stamped on the
 * record by the server as it commits the attack. Null when the system has no attack rule or no such defence. An
 * outcome the system names no word for is shown by its key.
 */
export function attackRuling(combat, defence, sources, borrowed) {
    const a = combat.attack;
    // Against the system's fixed number (issue 1312) there is no defence of the target's to find.
    const d = a?.against === 'number' ? { label: NUMBER_LABEL } : combat.defences.find((x) => x.key === defence);
    if (!a || !d)
        return null;
    const o = combat.outcomes;
    // Which homebrew layer set each part the working shows (issue 1310), from the registry's sources.
    const by = (paths) => {
        const s = sourceOf(sources, paths);
        return s ? { name: s.name, shown: s.released } : null;
    };
    const from = {};
    const dice = by(['/combat/attack/dice/count', '/combat/attack/dice/sides', '/combat/attack/dice/explode', '/combat/attack/dice/keep', '/combat/attack/dice/success']);
    const natural = by(['/combat/attack/natural']);
    const critical = a.critical ? by(['/combat/attack/critical/multiplier', '/combat/attack/critical/threat', '/combat/attack/critical/confirm']) : null;
    if (dice)
        from.dice = dice;
    if (natural)
        from.natural = natural;
    if (critical)
        from.critical = critical;
    return {
        dice: copyDice(a.dice), natural: a.natural.map((n) => ({ ...n })), critical: a.critical ? { confirm: a.critical.confirm, ...(a.critical.multiplies ? { multiplies: a.critical.multiplies } : {}) } : null,
        minimumDamage: combat.minimumDamage, ...(combat.minimumDamageKind && combat.minimumDamageKind !== PLAIN_DAMAGE && combat.minimumDamage !== null ? { minimumKind: combat.minimumDamageKind } : {}), defence: d.label,
        words: { hit: o.hit ?? 'hit', miss: o.miss ?? 'miss', critical: o.critical ?? 'critical', threat: o.threat ?? 'threat' },
        ...(a.tiers.length ? { tiers: a.tiers.map((t) => ({ ...t })) } : {}),
        ...(a.against === 'number' ? { against: 'number' } : {}),
        ...(a.marginToDamage ? { marginToDamage: true } : {}),
        ...(Object.keys(from).length ? { from } : {}),
        ...(borrowed ? { borrowed } : {}),
    };
}
//# sourceMappingURL=attack.js.map